import ErrorBoundary from '@components/ErrorBoundary/ErrorBoundary';
import ErrorMessage from '@components/ErrorBoundary/ErrorMessage';
import Loader from '@components/Loader/Loader';
import AssetsSkeleton from '@components/sentinel/skeleton/AssetsSkeleton/AssetsSkeleton';
import ClaimsSkeleton from '@components/sentinel/skeleton/ClaimsSkeleton/ClaimsSkeleton';
import VideoDescriptionSkeleton from '@components/sentinel/skeleton/VideoDescriptionSkeleton/VideoDescriptionSkeleton';
import VideoDetectedClaimsSkeleton from '@components/sentinel/skeleton/VideoDetectedClaimsSkeleton/VideoDetectedClaimsSkeleton';
import VideoMetadataSkeleton from '@components/sentinel/skeleton/VideoMetadataSkeleton/VideoMetadataSkeleton';
import VideoAnalysisDialog from '@components/sentinel/VideoAnalysisDialog/VideoAnalysisDialog';
import VideoDescription from '@components/sentinel/VideoDescription/VideoDescription';
import VideoDetectedClaims from '@components/sentinel/VideoDetectedClaims/VideoDetectedClaims';
import VideoMetadata from '@components/sentinel/VideoMetadata/VideoMetadata';
import useCurrentLang from '@hooks/useCurrentLang';
import { useStreamDetect } from '@hooks/useStreamDetect';
import { formatTimestamp } from '@utils/timeUtils';
import { Suspense, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useAppSelector } from 'universal-scripts';
import ErrorStep from '../ErrorStep/ErrorStep';

import './DetectionStep.css';

export default function DetectionStep() {
	const videoUrl = useAppSelector((state) => state.sentinel.videoUrl);
	const refreshCache = useAppSelector((state) => state.sentinel.refreshCache);
	const lang = useCurrentLang();
	const status = useAppSelector((state) => state.sentinel.phases.detect.status);
	const videoDescription = useAppSelector((state) => state.sentinel.phases.detect.videoDescription);
	const allClaims = useAppSelector((state) => state.sentinel.phases.detect.allClaims);
	const selectedClaims = useAppSelector((state) => state.sentinel.phases.detect.selectedClaims);
	const attacks = useAppSelector((state) => state.sentinel.phases.detect.attacks);
	const assets = useAppSelector((state) => state.sentinel.phases.detect.assets);

	useStreamDetect(videoUrl, lang, refreshCache);

	const showSkeletons = status === 'processing';

	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [dialogInitialTab, setDialogInitialTab] = useState<'description' | 'claims'>('description');

	const openDialog = (tab: 'description' | 'claims') => {
		setDialogInitialTab(tab);
		setIsDialogOpen(true);
	};

	const assetsList = useMemo(() => {
		return assets.map((asset, i) => (
			<li className={`asset-item asset-item--${asset.media_type}`} key={`asset-${i}`}>
				<span title={`${asset.media_type}`} className={`icon icon-${asset.media_type}`} />
				<small>{formatTimestamp(asset.timestamp_range.start.minutes, asset.timestamp_range.start.seconds)}</small>
				<strong>{asset.name}</strong>
			</li>
		));
	}, [assets]);

	const attacksList = useMemo(() => {
		return attacks.map((attack, i) => (
			<li title={`${attack.company} - ${attack.product}`} className="attack-item" key={`attack-${i}`}>
				<span>
					<strong>
						<span>{formatTimestamp(attack.timestamp_range.start.minutes, attack.timestamp_range.start.seconds)}</span>
					</strong>
					<span title={attack.claim} className="ellipsis">
						{attack.claim}
					</span>
				</span>
			</li>
		));
	}, [attacks]);

	const selectedClaimsList = useMemo(() => {
		return selectedClaims.map((claim, i) => {
			const claimId = claim.id !== undefined ? claim.id : i;
			const displayIndex = claimId + 1;

			return (
				<li className="selected-claim-item" key={`sel-claim-${claimId}`}>
					<header>
						<strong>{displayIndex}</strong>
					</header>
					<h4 title={claim.claim}>
						<span>{claim.claim}</span>
					</h4>
					<div className="category-pill">{claim.category}</div>
					<p>{claim.explanation}</p>
					<p className="impact-text">{claim.impact}</p>
				</li>
			);
		});
	}, [selectedClaims]);

	if (status === 'idle') {
		return <Loader />;
	}

	if (status === 'error') {
		return <ErrorStep />;
	}

	return (
		<>
			<div id="detection-step" className={`sentinel-step ${status === 'completed' ? 'sentinel-step--completed' : ''}`}>
				<div id="card-video-info">
					<div className="left">
						<ErrorBoundary errorComponent={ErrorMessage}>
							<Suspense fallback={<VideoMetadataSkeleton />}>
								<VideoMetadata />
							</Suspense>
						</ErrorBoundary>
					</div>
					<div className="right">
						{videoDescription ? (
							<VideoDescription onViewDetails={() => openDialog('description')} text={videoDescription} />
						) : (
							showSkeletons && <VideoDescriptionSkeleton />
						)}
						{allClaims.length > 0 ? (
							<VideoDetectedClaims onViewDetails={() => openDialog('claims')} claims={allClaims} />
						) : (
							showSkeletons && <VideoDetectedClaimsSkeleton />
						)}
					</div>
				</div>
				<h2>
					<FormattedMessage id="sentinel.content-analysis.title" />
				</h2>
				<div className="sentinel-note" id="content-analysis">
					{assets.length > 0 ? (
						<div id="detected-assets">
							<h3>
								<FormattedMessage id="sentinel.content-analysis.detected-assets.title" /> ({assets.length})
							</h3>
							<ul className="assets-list">{assetsList}</ul>
						</div>
					) : (
						showSkeletons && <AssetsSkeleton />
					)}

					{attacks.length > 0 ? (
						<div id="disinformation-attacks">
							<h3>
								<FormattedMessage id="sentinel.content-analysis.disinformation-attacks.title" /> ({attacks.length})
							</h3>
							<ul className="attacks-list">{attacksList}</ul>
						</div>
					) : (
						showSkeletons && <VideoDetectedClaimsSkeleton />
					)}
					{selectedClaims.length > 0 ? (
						<div id="selected-claims">
							<h3>
								<FormattedMessage id="sentinel.content-analysis.selected-claims.title" /> ({selectedClaims.length})
							</h3>
							<ul className="selected-claim-list">{selectedClaimsList}</ul>
						</div>
					) : (
						showSkeletons && <ClaimsSkeleton />
					)}
				</div>
			</div>

			<ErrorBoundary errorComponent={ErrorMessage}>
				<Suspense fallback={<div />}>
					<VideoAnalysisDialog initialTab={dialogInitialTab} isOpen={isDialogOpen} onClose={() => setIsDialogOpen(false)} />
				</Suspense>
			</ErrorBoundary>
		</>
	);
}
